// Customer Banner Dashboard
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { CAMPAIGN_TYPES, getCampaignDaysRemaining } from '../../data/campaignModels';
import AnalyticsDashboard from './AnalyticsDashboard';

const BannerDashboard = () => {
  const { currentUser } = useAuth();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [activeTab, setActiveTab] = useState('list'); // list, analytics

  useEffect(() => {
    if (currentUser) {
      loadMyBanners();
    }
  }, [currentUser]);

  const loadMyBanners = async () => {
    try {
      setLoading(true);
      const campaignsRef = collection(db, 'campaigns');
      const q = query(
        campaignsRef,
        where('type', '==', CAMPAIGN_TYPES.BANNER_RENTAL),
        where('rental.customerId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      const snapshot = await getDocs(q);
      const campaignsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setCampaigns(campaignsData);
    } catch (error) {
      console.error('Error loading banners:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const colors = {
      active: 'bg-green-100 text-green-800',
      'pending-approval': 'bg-yellow-100 text-yellow-800',
      approved: 'bg-blue-100 text-blue-800',
      paused: 'bg-orange-100 text-orange-800',
      expired: 'bg-gray-100 text-gray-600'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${colors[status] || 'bg-gray-100'}`}>
        {status === 'active' ? 'ใช้งาน' :
         status === 'pending-approval' ? 'รออนุมัติ' :
         status === 'approved' ? 'อนุมัติแล้ว' :
         status === 'paused' ? 'หยุดชั่วคราว' :
         status === 'expired' ? 'หมดอายุ' : status}
      </span>
    );
  };

  if (!currentUser) {
    return (
      <div className="p-6 text-center text-gray-500">
        กรุณาเข้าสู่ระบบ
      </div>
    );
  }

  if (loading) {
    return (
      <div className="p-6 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        <p className="mt-2 text-gray-500">กำลังโหลด...</p>
      </div>
    );
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">📢 Banner Dashboard ของฉัน</h1>

      {/* Tabs */}
      <div className="mb-6 border-b">
        <nav className="flex space-x-4">
          <button
            onClick={() => setActiveTab('list')}
            className={`py-2 px-4 font-medium border-b-2 transition-colors ${
              activeTab === 'list'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            Banner ของฉัน
          </button>
          {selectedCampaign && (
            <button
              onClick={() => setActiveTab('analytics')}
              className={`py-2 px-4 font-medium border-b-2 transition-colors ${
                activeTab === 'analytics'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              Analytics
            </button>
          )}
        </nav>
      </div>

      {/* Content */}
      {activeTab === 'list' && (
        <div>
          {campaigns.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow">
              <div className="text-gray-400 text-4xl mb-2">📢</div>
              <p className="text-gray-500">คุณยังไม่มี Banner ที่เช่า</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campaigns.map((campaign) => {
                const daysRemaining = getCampaignDaysRemaining(campaign);
                const startDate = campaign.startDate?.toDate?.() || new Date(campaign.startDate);
                const endDate = campaign.endDate?.toDate?.() || new Date(campaign.endDate);
                
                return (
                  <div
                    key={campaign.id}
                    className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => {
                      setSelectedCampaign(campaign);
                      setActiveTab('analytics');
                    }}
                  >
                    {campaign.banner?.desktop?.th?.imageUrl && (
                      <img
                        src={campaign.banner.desktop.th.imageUrl}
                        alt={campaign.name?.th || campaign.name?.en}
                        className="w-full h-32 object-cover rounded-t-lg"
                        onError={(e) => {
                          e.target.src = 'https://via.placeholder.com/400x200?text=Banner';
                        }}
                      />
                    )}
                    <div className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="font-semibold text-gray-800 truncate">
                          {campaign.name?.th || campaign.name?.en || 'ไม่มีชื่อ'}
                        </h3>
                        {getStatusBadge(campaign.status)}
                      </div>
                      
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>เริ่ม: {startDate.toLocaleDateString('th-TH')}</div>
                        <div>สิ้นสุด: {endDate ? endDate.toLocaleDateString('th-TH') : 'ไม่จำกัด'}</div>
                        {daysRemaining !== null && campaign.status === 'active' && (
                          <div className="text-orange-600 font-medium">
                            เหลือ {daysRemaining} วัน
                          </div>
                        )}
                        <div className="pt-2 border-t">
                          <div>👁️ Views: {campaign.analytics?.views || 0}</div>
                          <div>👆 Clicks: {campaign.analytics?.clicks || 0}</div>
                          {campaign.analytics?.views > 0 && (
                            <div className="text-xs text-gray-500">
                              CTR: {((campaign.analytics?.clicks || 0) / campaign.analytics.views * 100).toFixed(2)}%
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && selectedCampaign && (
        <AnalyticsDashboard campaign={selectedCampaign} />
      )}
    </div>
  );
};

export default BannerDashboard;

